import * as Oazapfts from 'oazapfts/lib/runtime'
import * as QS from 'oazapfts/lib/runtime/query'
import { defaults } from '@/api'

const oazapfts = Oazapfts.runtime(defaults)

export type BillingCycle = 'monthly' | 'annual'
export type CreditThreshold = 'normal' | 'low' | 'exhausted'
export type EnrollmentReason = 'active' | 'manual_billing' | 'not_configured'
export type SubscriptionActivation = 'pending' | 'active'
export type PlanId = 'starter' | 'growth' | 'enterprise'

export type BillingRates = {
  declarationCost: number
  articleCost: number
  extraCreditPrice: number
  setupFee: number
  rolloverMonths: number
  customPricing: boolean
}

export type CreditsSnapshot = {
  balance: number
  /** Remaining credits from plan subscription grants (not plan envelope). */
  included: number
  /** Remaining credits from pack purchases. */
  purchased: number
  /** Remaining credits from admin gifts. */
  gifted: number
  /** Consumption vs active ledger pool. */
  consumedEstimate: number
  /** Total remaining balance (included + purchased + gifted). */
  remaining: number
  /** Usage vs active ledger pool. */
  usagePercent: number
  threshold: CreditThreshold
}

export type BillingSubscription = {
  enrolled: boolean
  enrollmentReason: EnrollmentReason
  /** Absent on older payloads → treat as active when enrolled + credits present. */
  subscriptionActivation?: SubscriptionActivation | null
  /** True when a card (or payment method) is ready on Hyperline. */
  paymentMethodReady?: boolean | null
  plan: { id: PlanId; name: string; maxUsers: number | null } | null
  billingCycle: BillingCycle | null
  currency: string
  amountDue: number | null
  annualSavingsPercent: number | null
  annualSavingsAmount: number | null
  nextBillingAt: string | null
  credits: CreditsSnapshot | null
  rates: BillingRates | null
  mode?: string
}

export type CreditPack = {
  credits: number
  pricePerCredit: number
  priceEur: number
}

export type BillingCredits = {
  enrolled: boolean
  enrollmentReason: EnrollmentReason
  balance: number
  declarationsRemaining: number
  packs: CreditPack[]
  rates: BillingRates | null
  credits: CreditsSnapshot | null
  mode?: string
}

export type BillingPlan = {
  id: PlanId
  name: string
  active: boolean
  selectable: boolean
  recommended: boolean
  fee: number
  annualSavingsAmount: number
  envelopeCredits: number
  maxUsers: number | null
  rates: {
    declarationCost: number
    articleCost: number
    extraCreditPrice: number
  }
}

export type BillingPlansResponse = {
  cycle: BillingCycle
  annualDiscountPercent: number
  plans: BillingPlan[]
}

export type SubscriptionMutationResponse = {
  planId: PlanId
  billingCycle: BillingCycle
  effectiveAt: 'next_renewal' | string
  hyperlineSynced: boolean
}

export type PurchaseCreditsResponse = {
  quantity: number
  unitPrice: number
  total: number
  priceId: string
  credits: { enrolled: boolean; balance: number }
  mode?: string
}

export type BillingComponentToken = {
  token: string
  mode: 'sandbox' | 'production' | string
}

export type PaymentMethodBrand = 'visa' | 'mastercard' | 'amex' | 'other'
export type PaymentMethodStatus =
  | 'active'
  | 'pending'
  | 'expired'
  | 'errored'

export type BillingPaymentMethod = {
  id: string
  brand: PaymentMethodBrand | string
  last4: string
  expMonth: number
  expYear: number
  holderName: string | null
  isDefault: boolean
  status: PaymentMethodStatus | string
}

export type BillingPaymentMethodsResponse = {
  items: BillingPaymentMethod[]
}

export type BillingPaymentMethodSetup = {
  kind: 'stripe_setup' | string
  mode: 'sandbox' | 'production' | string
  publicKey: string
  intentSecret: string
  intentId: string
  clientProviderId: string
  providerName: string
}

export type ConfirmBillingPaymentMethodBody = {
  providerPaymentMethodId: string
  intentId: string
  clientProviderId: string
  paymentMethodType?: 'card' | string
  state?: 'active' | string
}

export type ConfirmBillingPaymentMethodResult = {
  id: string
  status: PaymentMethodStatus | string
}

export type BillingInvoiceStatus = 'paid' | 'pending' | 'failed'

export type BillingInvoice = {
  id: string
  number: string
  date: string
  periodLabel: string | null
  periodStartsAt: string | null
  periodEndsAt: string | null
  amountHtCents: number
  currency: string
  status: BillingInvoiceStatus | string
}

export type BillingInvoicesResponse = {
  items: BillingInvoice[]
}

export type BillingInvoiceDocument = {
  url: string
  expiresAt: string | null
}

export type BillingApiError = {
  errorMessage?: string
  errorHumanMessage?: string
}

export function getBillingErrorMessage(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') return undefined
  const data = (error as { data?: BillingApiError }).data
  return data?.errorMessage
}

export function getBillingErrorHumanMessage(
  error: unknown
): string | undefined {
  if (!error || typeof error !== 'object') return undefined
  const data = (error as { data?: BillingApiError }).data
  return data?.errorHumanMessage
}

export async function getBillingSubscription(): Promise<BillingSubscription> {
  return oazapfts.ok(
    oazapfts.fetchJson('/billing/subscription', { method: 'GET' })
  )
}

export async function getBillingCredits(): Promise<BillingCredits> {
  return oazapfts.ok(
    oazapfts.fetchJson('/billing/credits', { method: 'GET' })
  )
}

export async function getBillingPlans(
  cycle?: BillingCycle
): Promise<BillingPlansResponse> {
  const query = QS.query(
    QS.explode({
      ...(cycle ? { cycle } : {}),
    })
  )
  return oazapfts.ok(
    oazapfts.fetchJson(`/billing/plans${query}`, { method: 'GET' })
  )
}

export async function activateSubscription(body?: {
  planId?: Exclude<PlanId, 'enterprise'>
  cycle?: BillingCycle
}): Promise<BillingSubscription> {
  return oazapfts.ok(
    oazapfts.fetchJson('/billing/subscription/activate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body ?? {}),
    })
  )
}

export async function putBillingCycle(
  cycle: BillingCycle
): Promise<SubscriptionMutationResponse> {
  return oazapfts.ok(
    oazapfts.fetchJson('/billing/subscription/cycle', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cycle }),
    })
  )
}

export async function putBillingPlan(
  planId: Exclude<PlanId, 'enterprise'>
): Promise<SubscriptionMutationResponse> {
  return oazapfts.ok(
    oazapfts.fetchJson('/billing/subscription/plan', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ planId }),
    })
  )
}

export async function purchaseCredits(
  quantity: number,
  idempotencyKey: string
): Promise<PurchaseCreditsResponse> {
  return oazapfts.ok(
    oazapfts.fetchJson('/billing/credits/purchase', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Idempotency-Key': idempotencyKey,
      },
      body: JSON.stringify({ quantity }),
    })
  )
}

/** @deprecated Prefer postBillingPaymentMethodSetup for Facturation page. */
export async function postBillingComponentToken(): Promise<BillingComponentToken> {
  return oazapfts.ok(
    oazapfts.fetchJson('/billing/component-token', {
      method: 'POST',
    })
  )
}

export async function getBillingPaymentMethods(): Promise<BillingPaymentMethodsResponse> {
  return oazapfts.ok(
    oazapfts.fetchJson('/billing/payment-methods', { method: 'GET' })
  )
}

export async function postBillingPaymentMethodSetup(): Promise<BillingPaymentMethodSetup> {
  return oazapfts.ok(
    oazapfts.fetchJson('/billing/payment-methods/setup', {
      method: 'POST',
    })
  )
}

export async function postBillingPaymentMethodConfirm(
  body: ConfirmBillingPaymentMethodBody
): Promise<ConfirmBillingPaymentMethodResult> {
  return oazapfts.ok(
    oazapfts.fetchJson('/billing/payment-methods', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        paymentMethodType: 'card',
        state: 'active',
        ...body,
      }),
    })
  )
}

export async function postBillingPaymentMethodSetDefault(
  id: string
): Promise<{ id: string; isDefault: boolean }> {
  return oazapfts.ok(
    oazapfts.fetchJson(`/billing/payment-methods/${encodeURIComponent(id)}/set-default`, {
      method: 'POST',
    })
  )
}

export async function deleteBillingPaymentMethod(
  id: string
): Promise<void | { deleted: boolean }> {
  const res = await oazapfts.fetchJson(
    `/billing/payment-methods/${encodeURIComponent(id)}`,
    { method: 'DELETE' }
  )
  if (res.status === 204) return
  return oazapfts.ok(Promise.resolve(res))
}

export async function getBillingInvoices(): Promise<BillingInvoicesResponse> {
  return oazapfts.ok(
    oazapfts.fetchJson('/billing/invoices', { method: 'GET' })
  )
}

export async function getBillingInvoiceDocument(
  id: string
): Promise<BillingInvoiceDocument> {
  return oazapfts.ok(
    oazapfts.fetchJson(
      `/billing/invoices/${encodeURIComponent(id)}/document`,
      { method: 'GET' }
    )
  )
}
